import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, homeFor } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Navigate } from "react-router-dom";

export default function Login({ mode = "login" }) {
  const { user, login, register, brand } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const isRegister = mode === "register";

  if (user) return <Navigate to={homeFor(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = isRegister ? await register(form.name, form.email, form.password) : await login(form.email, form.password);
      navigate(homeFor(u));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fill = (email, password) => setForm({ name: "", email, password });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:flex flex-col justify-between bg-[#0F172A] text-white p-12 grain overflow-hidden">
        <div className="relative">
          <img src={brand.logo_light} alt={brand.agency_name} className="h-16 w-auto" data-testid="login-logo-dark-panel" />
        </div>
        <div className="relative max-w-lg">
          <div className="overline-label text-blue-400 mb-4">One system. Whole agency.</div>
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold tracking-tight leading-[1.05]">See everything happening. Know exactly what's next.</h1>
          <p className="text-slate-400 mt-5 leading-relaxed">Clients, projects, tasks, goals, proposals, invoices and Google Drive deliverables — in one command center with a clear view for owners, teams and clients.</p>
          <div className="grid grid-cols-3 gap-4 mt-10">
            {[["Admin", "Full P&L, pipeline, workload"], ["Team", "Prioritised next-up tasks"], ["Clients", "Live status, approve proposals"]].map(([t, s]) => (
              <div key={t} className="border border-white/10 rounded-lg p-4 bg-white/5">
                <div className="font-heading font-semibold">{t}</div>
                <div className="text-xs text-slate-400 mt-1">{s}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© {new Date().getFullYear()} {brand.agency_name}</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <div className="w-full max-w-md fade-up">
          <img src={brand.logo_dark} alt={brand.agency_name} className="h-12 w-auto mb-8" data-testid="login-logo" />
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{isRegister ? "Create your account" : "Welcome back"}</h2>
          <p className="text-sm text-slate-500 mt-2">{isRegister ? `Clients register here. ${brand.agency_name} will link this login to your account (Google sign-in links automatically).` : `Sign in to your ${brand.agency_name} workspace.`}</p>

          <Button type="button" variant="outline" className="w-full mt-8 h-11 bg-white" onClick={googleLogin} data-testid="google-login-btn">
            <svg width="18" height="18" viewBox="0 0 48 48" className="mr-2"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.8-6C.9 16.6 0 20.2 0 24s.9 7.4 2.6 10.7l7.8-6z"/><path fill="#34A853" d="M24 48c6.2 0 11.6-2 15.4-5.6l-7.5-5.8c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.7-4.1-13.6-9.8l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
            Continue with Google
          </Button>
          <div className="flex items-center gap-3 my-6 text-xs text-slate-400"><div className="h-px flex-1 bg-slate-200" />or with email<div className="h-px flex-1 bg-slate-200" /></div>

          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div className="space-y-1.5"><Label htmlFor="name">Full name</Label><Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input" /></div>
            )}
            <div className="space-y-1.5"><Label htmlFor="email">Email</Label><Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="password">Password</Label><Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" /></div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-slate-900 hover:bg-slate-800" data-testid="login-submit-btn">
              {busy ? "Please wait…" : isRegister ? "Create account" : "Sign in"} <ArrowRight size={16} className="ml-1" />
            </Button>
          </form>

          <p className="text-sm text-slate-500 mt-6">
            {isRegister ? <>Already have an account? <Link to="/login" className="text-blue-600 font-medium" data-testid="goto-login-link">Sign in</Link></> : <>Client without an account? <Link to="/register" className="text-blue-600 font-medium" data-testid="goto-register-link">Register</Link></>}
          </p>

          {!isRegister && brand.demo_present && (
            <div className="mt-8 panel p-4" data-testid="demo-accounts">
              <div className="overline-label mb-3">Demo accounts</div>
              <div className="grid grid-cols-3 gap-2">
                {[["Admin", "admin@sugarcubes.co.in", "Admin@123"], ["Employee", "alex@sugarcubes.co.in", "Employee@123"], ["Client", "client@neptune.com", "Client@123"]].map(([r, e, p]) => (
                  <button key={r} type="button" onClick={() => fill(e, p)} className="text-left border border-slate-200 rounded-md px-3 py-2 hover:border-blue-400 hover:bg-blue-50/40 transition-colors" data-testid={`demo-${r.toLowerCase()}-btn`}>
                    <div className="text-xs font-semibold text-slate-800">{r}</div>
                    <div className="text-[10px] text-slate-500 truncate">{e}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
